import { useEffect, useState } from "react";
import Section from "../../parts/Section";
import DiscreetMessage from "../../parts/DiscreetMessage";
import Api from "../../Api";
import _ from "lodash";
import moment from "moment";
import { intToDec } from "../../utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { StatsData } from "../../types";
import classNames from "classnames";

const boundaries = {
  start: { month: "2020-01", year: "2020" },
  end: { month: moment().format("YYYY-MM"), year: moment().format("YYYY") },
};
const monthOptions = _.range(
  0,
  moment().diff(boundaries.start.month, "months") + 1
)
  .map((i) => moment().subtract(i, "months"))
  .map((moment) => ({
    label: moment.format("YYYY/MM"),
    value: moment.format("YYYY-MM"),
  }));
const yearOptions = _.range(
  moment().year(),
  parseInt(boundaries.start.year) - 1
);

const Stats = () => {
  const [period, setPeriod] = useState(moment().format("YYYY-MM"));
  const [groupBy, setGroupBy] = useState<"events" | "creation">("events");
  const [stats, setStats] = useState<StatsData | null>(null);

  useEffect(() => {
    setStats(null);
    const timer = setTimeout(() => {
      Api.statsGet(period, groupBy).then(setStats);
    }, 500);

    return () => clearTimeout(timer);
  }, [period, groupBy]);

  return (
    <Section className="p-4 pb-32 flex flex-col gap-4">
      <PeriodPicker
        period={period}
        setPeriod={setPeriod}
        groupBy={groupBy}
        setGroupBy={setGroupBy}
      />

      {!stats && <DiscreetMessage text="Carregando ..." />}

      {stats && (
        <>
          <div className="border">
            <div className="px-2 py-1 font-bold text-center text-lg border-b">
              Valores
            </div>

            <div className="text-sm text-center font-semibold border-b flex">
              <div className="basis-1/3 py-0.5 border-r">Venda</div>
              <div className="basis-1/3 py-0.5 border-r">Mensal</div>
              <div className="basis-1/3 py-0.5">Serviço</div>
            </div>

            <Amounts
              sale={stats.amounts.sale}
              rent={stats.amounts.rent}
              fee={stats.amounts.fee}
            />

            {stats.amountsBySeller.map((amounts, i) => (
              <div key={i} className="border-t">
                <div className="px-2 py-1 text-xs text-gray-600 font-semibold text-center border-b">
                  {amounts.name}
                </div>

                <Amounts
                  sale={amounts.sale}
                  rent={amounts.rent}
                  fee={amounts.fee}
                />
              </div>
            ))}
          </div>

          <div className="border">
            <div className="px-2 py-1 font-bold text-center text-lg border-b">
              Orçamentos
            </div>

            <div className="text-sm text-center font-semibold border-b flex">
              <div className="basis-1/3 py-0.5 border-r">Enviados</div>
              <div className="basis-1/3 py-0.5 border-r">Autorizados</div>
              <div className="basis-1/3 py-0.5">Cancelados</div>
            </div>

            <div className="flex font-bold">
              <div className="basis-1/3 py-1 px-2 border-r text-right ">
                {stats.counts.sent}
              </div>
              <div className="basis-1/3 py-1 px-2 border-r flex items-center justify-end">
                {groupBy === "creation" && (
                  <span className="text-xs font-light mr-1 text-gray-500">
                    (
                    {(
                      (stats.counts.authorized / stats.counts.sent) *
                      100
                    ).toFixed(1)}
                    %)
                  </span>
                )}
                {stats.counts.authorized}
              </div>
              <div className="basis-1/3 py-1 px-2 flex items-center justify-end">
                {groupBy === "creation" && stats.counts.authorized > 0 && (
                  <span className="text-xs font-light mr-1 text-gray-500">
                    (
                    {(
                      (stats.counts.cancelled / stats.counts.authorized) *
                      100
                    ).toFixed(1)}
                    %)
                  </span>
                )}
                {stats.counts.cancelled}
              </div>
            </div>

            {stats.countsBySeller.map((counts, i) => (
              <div key={i} className="border-t text-gray-600">
                <div className="px-2 py-1 text-xs font-semibold text-center border-b">
                  {counts.name}
                </div>

                <div className="flex text-sm font-bold">
                  <div className="basis-1/3 py-1 px-2 border-r text-right">
                    {counts.sent}
                  </div>
                  <div className="basis-1/3 py-1 px-2 border-r flex items-center justify-end">
                    {groupBy === "creation" && (
                      <span className="text-xs font-light mr-1 text-gray-500">
                        ({((counts.authorized / counts.sent) * 100).toFixed(1)}
                        %)
                      </span>
                    )}
                    {counts.authorized}
                  </div>
                  <div className="basis-1/3 py-1 px-2 flex items-center justify-end">
                    {groupBy === "creation" && counts.authorized > 0 && (
                      <span className="text-xs font-light mr-1 text-gray-500">
                        (
                        {((counts.cancelled / counts.authorized) * 100).toFixed(
                          1
                        )}
                        %)
                      </span>
                    )}
                    {counts.cancelled}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </Section>
  );
};

export default Stats;

const PeriodPicker = ({
  period,
  setPeriod,
  groupBy,
  setGroupBy,
}: {
  period: string;
  setPeriod: (value: string) => void;
  groupBy: string;
  setGroupBy: (value: "events" | "creation") => void;
}) => {
  const [byMonth, setByMonth] = useState(1);

  const dec = () => {
    setPeriod(
      moment(period)
        .subtract(1, byMonth ? "month" : "year")
        .format(byMonth ? "YYYY-MM" : "YYYY")
    );
  };

  const inc = () => {
    setPeriod(
      moment(period)
        .add(1, byMonth ? "month" : "year")
        .format(byMonth ? "YYYY-MM" : "YYYY")
    );
  };

  const changeRangeType = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const isByMonth = JSON.parse(e.target.value);
    setByMonth(isByMonth);
    isByMonth
      ? setPeriod(moment().format("YYYY-MM"))
      : setPeriod(moment().format("YYYY"));
  };

  return (
    <div className="p-2 rounded-lg border text-sm">
      <div className="w-full flex mb-2">
        <select
          className="border rounded px-2 py-1 mr-4"
          value={byMonth}
          onChange={changeRangeType}
        >
          <option value={1}>Mês</option>
          <option value={0}>Ano</option>
        </select>

        {!!byMonth && (
          <select
            className="border rounded px-2 py-1 mr-4 flex-1"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
          >
            {monthOptions.map(({ label, value }) => (
              <option value={value} key={value}>
                {label}
              </option>
            ))}
          </select>
        )}

        {!byMonth && (
          <select
            className="border rounded px-2 py-1 mr-4 flex-1"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
          >
            {yearOptions.map((year) => (
              <option value={year} key={year}>
                {year}
              </option>
            ))}
          </select>
        )}

        <button
          className="border rounded-l px-4 text-lg disabled:text-gray-200 bg-white hover:bg-slate-100"
          disabled={period === boundaries.start[byMonth ? "month" : "year"]}
          onClick={dec}
        >
          <FontAwesomeIcon icon="chevron-left" />
        </button>

        <button
          className="border-y border-r rounded-r px-4 text-lg disabled:text-gray-200 bg-white hover:bg-slate-100"
          disabled={period === boundaries.end[byMonth ? "month" : "year"]}
          onClick={inc}
        >
          <FontAwesomeIcon icon="chevron-right" />
        </button>
      </div>

      <div className="flex w-full items-center justify-center">
        <div className="mr-2">Agrupamento por</div>
        <button
          className={classNames(
            "border px-2 py-1 w-[90px] bg-white hover:bg-slate-100 font-light border-r-0 rounded-l",
            { "font-semibold bg-slate-50": groupBy === "events" }
          )}
          onClick={() => setGroupBy("events")}
        >
          Eventos
        </button>
        <button
          className={classNames(
            "border px-2 py-1 w-[90px] bg-white hover:bg-slate-100 font-light rounded-r",
            {
              "font-semibold bg-slate-50": groupBy === "creation",
            }
          )}
          onClick={() => setGroupBy("creation")}
        >
          Criação
        </button>
      </div>
    </div>
  );
};

const Amounts = ({
  sale,
  rent,
  fee,
}: {
  sale: number;
  rent: number;
  fee: number;
}) => (
  <div className="flex text-sm font-mono tracking-tight text-right">
    <div className="basis-1/3 bg-cyan-50 text-cyan-800 p-2 border-r">
      {intToDec(sale)}
    </div>

    <div className="basis-1/3 bg-lime-50 text-lime-800 p-2 border-r">
      {intToDec(rent)}
    </div>

    <div className="basis-1/3 bg-violet-50 text-violet-900 p-2">
      {intToDec(fee)}
    </div>
  </div>
);
